import { Button } from "@/shared/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Calendar } from "@/shared/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface DateRangePickerProps {
  /** Currently selected single date */
  date: Date;
  /** Called when single date changes */
  onDateChange: (date: Date) => void;
  /** Currently selected date range (undefined = single date mode) */
  dateRange?: DateRange;
  /** Called when date range changes */
  onDateRangeChange?: (range: DateRange) => void;
  /** Whether to show preset buttons and range toggle (entries view only) */
  showPresets?: boolean;
  /** Compact mode: shorter date format (no year), for action bar placement */
  compact?: boolean;
  /** Called when user clicks previous navigation button */
  onPrevious?: () => void;
  /** Called when user clicks next navigation button */
  onNext?: () => void;
}

/**
 * Formats a date for display in the picker button.
 * @param date - The date to format
 * @param compact - If true, omits the year (e.g., "Jan 18" instead of "Jan 18, 2026")
 */
function formatDate(date: Date, compact = false): string {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(compact ? {} : { year: "numeric" }),
  });
}

/**
 * Date picker component that supports both single date and date range selection.
 *
 * When showPresets is true (entries view), displays:
 * - Quick preset buttons (Today, Work Week, This Month)
 * - Toggle between Single Date and Date Range modes
 *
 * @example
 * ```tsx
 * // Single date mode (history view)
 * <DateRangePicker
 *   date={date}
 *   onDateChange={setDate}
 * />
 *
 * // With range support (entries view)
 * <DateRangePicker
 *   date={date}
 *   onDateChange={setDate}
 *   dateRange={dateRange}
 *   onDateRangeChange={setDateRange}
 *   showPresets
 * />
 * ```
 */
export function DateRangePicker({
  date,
  onDateChange,
  dateRange,
  onDateRangeChange,
  showPresets = false,
  compact = false,
  onPrevious,
  onNext,
}: DateRangePickerProps) {
  const isRange = !!(dateRange?.from || dateRange?.to);
  const hasNavigation = !!(onPrevious || onNext);

  // Check if selected date/range is not today
  const today = new Date();
  const isToday = (d: Date) =>
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();

  const isNotToday = isRange
    ? !!(dateRange?.from && !isToday(dateRange.from)) || !!(dateRange?.to && !isToday(dateRange.to))
    : !isToday(date);

  // Preset handlers — each uses a single setter to avoid competing URL updates
  const setToday = () => {
    if (onDateRangeChange) {
      // Clearing range also clears the date param, defaulting back to today.
      // A single setSearchParams call avoids the race condition from two separate calls.
      onDateRangeChange({ from: undefined, to: undefined });
    } else {
      onDateChange(new Date());
    }
  };

  const setWorkWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    onDateRangeChange?.({ from: monday, to: friday });
  };

  const setThisMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    onDateRangeChange?.({ from: firstDay, to: lastDay });
  };

  const switchToSingleDate = () => {
    // Clear range; date defaults to today when no range params are present
    onDateRangeChange?.({ from: undefined, to: undefined });
  };

  const switchToDateRange = () => {
    onDateRangeChange?.({ from: date, to: date });
  };

  // Detect which preset is currently active
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const activePreset = (() => {
    if (!isRange && isToday(date)) return "today";
    if (isRange && dateRange?.from && dateRange?.to) {
      const now = new Date();
      const dow = now.getDay();
      const mon = new Date(now); mon.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1));
      const fri = new Date(mon); fri.setDate(mon.getDate() + 4);
      if (sameDay(dateRange.from, mon) && sameDay(dateRange.to, fri)) return "week";

      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      if (sameDay(dateRange.from, first) && sameDay(dateRange.to, last)) return "month";
    }
    return null;
  })();

  // Build display text
  const getDisplayText = () => {
    if (isRange) {
      if (dateRange?.from && dateRange?.to) {
        return `${formatDate(dateRange.from, compact)} - ${formatDate(dateRange.to, compact)}`;
      } else if (dateRange?.from) {
        return `${formatDate(dateRange.from, compact)} - End`;
      } else if (dateRange?.to) {
        return `Start - ${formatDate(dateRange.to, compact)}`;
      }
    }
    return formatDate(date, compact);
  };

  // Handle range selection — only update range, not single date
  const handleRangeSelect = (range: any) => {
    if (!range) return;
    const sameFrom = (dateRange?.from?.getTime() || 0) === (range.from?.getTime() || 0);
    const sameTo = (dateRange?.to?.getTime() || 0) === (range.to?.getTime() || 0);
    if (!sameFrom || !sameTo) {
      onDateRangeChange?.({ from: range.from, to: range.to });
    }
  };

  // The main popover trigger button
  const triggerButton = (
    <PopoverTrigger asChild>
      <Button
        variant="outline"
        className={cn(
          "justify-center text-center font-normal bg-transparent border-0 text-foreground hover:bg-transparent min-w-0 w-auto px-1",
          hasNavigation ? "rounded-none" : "bg-card border-border hover:bg-secondary"
        )}
      >
        <CalendarIcon className="mr-1 h-4 w-4 shrink-0" />
        <span className="truncate">{getDisplayText()}</span>
      </Button>
    </PopoverTrigger>
  );

  return (
    <Popover>
      {hasNavigation ? (
        <TooltipProvider delayDuration={300}>
          <div className="group flex items-center rounded-md border border-border bg-card hover:bg-secondary h-10 transition-colors relative">
            {/* Blue dot indicator when date is not today */}
            {isNotToday && (
              <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-blue-500 border-2 border-card" />
            )}
            {/* Previous button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onPrevious}
                  className="flex items-center justify-center w-7 h-full rounded-l-md transition-colors text-muted-foreground hover:text-foreground"
                  aria-label="Previous day"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Previous day</p>
              </TooltipContent>
            </Tooltip>

            {/* Date picker trigger */}
            <Tooltip>
              <TooltipTrigger asChild>
                {triggerButton}
              </TooltipTrigger>
              <TooltipContent>
                <p>Select date</p>
              </TooltipContent>
            </Tooltip>

            {/* Next button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onNext}
                  className="flex items-center justify-center w-7 h-full rounded-r-md transition-colors text-muted-foreground hover:text-foreground"
                  aria-label="Next day"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Next day</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      ) : (
        <div className="relative">
          {isNotToday && (
            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-blue-500 border-2 border-card z-10" />
          )}
          {triggerButton}
        </div>
      )}
      <PopoverContent className="w-auto p-0 overflow-hidden" align="center">
        {showPresets ? (
          <div className="p-4 space-y-3 flex flex-col items-center">
            {/* Single Date / Date Range tab selector */}
            <Tabs
              value={isRange ? "range" : "single"}
              onValueChange={(v) => v === "single" ? switchToSingleDate() : switchToDateRange()}
              className="w-full"
            >
              <TabsList className="w-full border border-border">
                <TabsTrigger value="single" className="flex-1 rounded-none rounded-l-md data-[state=active]:border data-[state=active]:border-border">Single Date</TabsTrigger>
                <TabsTrigger value="range" className="flex-1 rounded-none rounded-r-md data-[state=active]:border data-[state=active]:border-border">Date Range</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Preset buttons */}
            <div className="flex flex-row gap-2">
              {([
                { label: "Today", key: "today" as const, action: setToday },
                { label: "Work Week", key: "week" as const, action: setWorkWeek },
                { label: "This Month", key: "month" as const, action: setThisMonth },
              ]).map(({ label, key, action }) => (
                <button
                  key={key}
                  onClick={action}
                  className={cn(
                    "px-3 py-1 text-sm rounded border transition-colors",
                    activePreset === key
                      ? "text-white border-transparent"
                      : "bg-card text-foreground border-border hover:bg-secondary"
                  )}
                  style={activePreset === key ? { backgroundColor: "var(--brand-dark-green)" } : undefined}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Calendar */}
            {isRange ? (
              <Calendar
                mode="range"
                selected={dateRange as any}
                onSelect={handleRangeSelect}
                className="!p-0 [--cell-size:36px] [--primary:var(--brand-dark-green)] [--ring:transparent] [--accent:oklch(0.302_0.024_204.7_/_0.15)]"
                initialFocus
              />
            ) : (
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => onDateChange(d || date)}
                className="!p-0 [--cell-size:36px] [--primary:var(--brand-dark-green)] [--ring:transparent] [--accent:oklch(0.302_0.024_204.7_/_0.15)]"
                initialFocus
              />
            )}
          </div>
        ) : (
          // Simple single-date picker (history view)
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => onDateChange(d || date)}
            className="!bg-transparent !p-0 [--cell-size:36px] [--primary:var(--brand-dark-green)] [--ring:transparent] [--accent:oklch(0.302_0.024_204.7_/_0.15)]"
            initialFocus
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
